import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CLAUDE_MODELS, type MailLang, type MailSection } from '@goenhub/shared';
import { httpsCallable } from 'firebase/functions';
import {
  ArrowLeft,
  Bookmark,
  CalendarClock,
  Clipboard,
  Code as CodeIcon,
  Download,
  ExternalLink,
  FileInput,
  FileText,
  Mail as MailIcon,
  MessagesSquare,
  Save,
  Send,
  Sparkles,
  Wand2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AddSectionButton } from './AddSectionButton';
import { ImportMailDialog } from './ImportMailDialog';
import { SectionGapInserter } from './SectionGapInserter';
import { SortableSection } from './SortableSection';
import { SubjectCard } from './SubjectCard';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useAdmins } from '@/hooks/useAdmins';
import { useCreateMail, useUpdateMail } from '@/hooks/useMailData';
import { usePromptPresets, usePromptTemplates } from '@/hooks/usePromptData';
import { functions } from '@/lib/firebase';
import { buildMailto, downloadEml, toHtml, toPlainText } from '@/lib/mailRender';
import { cn } from '@/lib/utils';
import { ScheduleFormDialog } from '@/pages/mail/schedules/ScheduleFormDialog';
import { PresetFormDialog } from '@/pages/prompts/presets/PresetFormDialog';

const sendTestMailFn = httpsCallable<
  { mailId: string; ctaUrl?: string },
  { ok: boolean; messageId: string; to: string }
>(functions, 'sendTestMail');

const requestMailReviewFn = httpsCallable<
  { mailId: string; staff: { name: string; email: string }; comment: string },
  { ok: boolean; messageId: string }
>(functions, 'requestMailReview');

interface RestoreState {
  mailId: string;
  templateId: string;
  subject: string;
  sections: MailSection[];
  overview: string;
  field_comments: Record<string, string>;
  formData: Record<string, string>;
  lang: MailLang;
  modelUsed?: string;
}

interface GenerateResponse {
  ok: boolean;
  modelUsed: string;
  generated: {
    subject: string;
    sections: MailSection[];
    overview: string;
    field_comments: Record<string, string>;
  };
}

const generateFn = httpsCallable<
  { templateId: string; formValues: Record<string, string>; lang: MailLang; model?: string },
  GenerateResponse
>(functions, 'generateMail');

export function MailGeneratePage() {
  const { data: templates, isLoading: tplLoading } = usePromptTemplates({ scope: 'mail' });
  const createMail = useCreateMail();
  const updateMail = useUpdateMail();
  const location = useLocation();
  const navigate = useNavigate();
  const [templateId, setTemplateId] = useState('');
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [lang, setLang] = useState<MailLang>('ja');
  const [model, setModel] = useState<string>(CLAUDE_MODELS.sonnetDefault);
  const [generating, setGenerating] = useState(false);
  const [result, setResultState] = useState<GenerateResponse['generated'] | null>(null);
  /**
   * 最新の生成結果。カードの確定（窓外クリック）と保存ボタンが同じクリック内で続けて走っても、
   * 再描画を待たずに確定済みの内容を保存できるよう、state と同時に同期で更新する。
   */
  const resultRef = useRef<GenerateResponse['generated'] | null>(null);
  const setResult = useCallback((next: GenerateResponse['generated'] | null) => {
    resultRef.current = next;
    setResultState(next);
  }, []);
  /** 最新の結果を元に更新する（件名とセクションを同時に確定しても互いに上書きしない） */
  const updateResult = (
    fn: (current: GenerateResponse['generated']) => GenerateResponse['generated'],
  ) => {
    if (!resultRef.current) return;
    setResult(fn(resultRef.current));
  };
  /**
   * 編集中で未確定の入力（件名 / セクション本文 id 別）。保存ボタン押下時に自動で確定する。
   * 2026-10-09: 入力が確定されないまま保存され、件名「無題」・本文空のメールが保存された
   * （spec_mail_system.md B-19.3）。
   */
  const pendingDraftsRef = useRef<{ subject: string | null; sections: Record<string, string> }>({
    subject: null,
    sections: {},
  });
  const [savingMail, setSavingMail] = useState(false);
  const [presetId, setPresetId] = useState<string>('');
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingMailId, setEditingMailId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'text' | 'html'>('text');
  /** 復元トリガーされた templateId を保持。この値が templateId effect で一致すれば値クリアをスキップ */
  const restoredTemplateIdRef = useRef<string | null>(null);

  // v3.3: 左サイドのタブ (テンプレート / レビュー依頼)
  const [leftTab, setLeftTab] = useState<'template' | 'review'>('template');

  // v3.3: テスト送信 / 配信予約 / レビュー依頼 (MailListPage の MailDetailDialog から移管)
  const [sending, setSending] = useState<'test' | 'review' | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [reviewerComment, setReviewerComment] = useState('');
  // v3.3.2: レビュー担当者は admins (システム > ユーザー) から選択
  const { data: admins } = useAdmins();
  const reviewerOptions = (admins ?? [])
    .filter((a) => a.resolvedStatus === 'active' && a.email)
    .map((a) => ({ email: a.email, name: a.displayName || a.fullName || a.email }));

  const handleSendTest = async () => {
    if (!editingMailId) {
      toast.error('テスト送信には先にメールを保存してください');
      return;
    }
    setSending('test');
    try {
      const res = await sendTestMailFn({ mailId: editingMailId });
      toast.success(`テスト送信: ${res.data.to}`);
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSending(null);
    }
  };

  const handleRequestReview = async () => {
    if (!editingMailId) {
      toast.error('レビュー依頼には先にメールを保存してください');
      return;
    }
    if (!reviewerEmail) {
      toast.error('レビュー担当者を選択してください');
      return;
    }
    setSending('review');
    try {
      await requestMailReviewFn({
        mailId: editingMailId,
        staff: { name: reviewerName, email: reviewerEmail },
        comment: reviewerComment,
      });
      toast.success('レビュー依頼を送信しました');
      setReviewerEmail('');
      setReviewerName('');
      setReviewerComment('');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSending(null);
    }
  };

  // ドラッグ&ドロップ並べ替え用センサー
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    updateResult((r) => {
      const oldIdx = r.sections.findIndex((s) => s.id === active.id);
      const newIdx = r.sections.findIndex((s) => s.id === over.id);
      if (oldIdx < 0 || newIdx < 0) return r;
      return { ...r, sections: arrayMove(r.sections, oldIdx, newIdx) };
    });
  };

  /** insertAt 位置に新規セクションを挿入（末尾なら sections.length を渡す） */
  const insertSection = (insertAt: number, type: MailSection['type']) => {
    const labels: Record<MailSection['type'], string> = {
      greeting: '冒頭あいさつ',
      lead: 'リード文',
      body: '本文',
      cta: 'CTA',
      closing: '締め',
      ps: '追伸',
    };
    const newSection: MailSection = {
      id: `sec_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      type,
      label: labels[type],
      content: '',
      ...(type === 'cta' ? { cta_url_placeholder: '', cta_button_label: '' } : {}),
    };
    updateResult((r) => {
      const next = [...r.sections];
      next.splice(insertAt, 0, newSection);
      return { ...r, sections: next };
    });
  };

  /** 未確定の入力を結果へ反映して返す（保存ボタン用）。反映した場合は画面の結果も更新する */
  const commitPendingDrafts = () => {
    const current = resultRef.current;
    if (!current) return null;
    const { subject, sections } = pendingDraftsRef.current;
    const hasSectionDraft = current.sections.some(
      (s) => sections[s.id] !== undefined && sections[s.id] !== s.content,
    );
    const nextSubject = subject !== null ? subject.trim() : current.subject;
    if (nextSubject === current.subject && !hasSectionDraft) return current;
    const next = {
      ...current,
      subject: nextSubject,
      sections: current.sections.map((s) =>
        sections[s.id] !== undefined ? { ...s, content: sections[s.id] } : s,
      ),
    };
    setResult(next);
    return next;
  };

  const setSectionDraft = (id: string, draft: string | null) => {
    if (draft === null) delete pendingDraftsRef.current.sections[id];
    else pendingDraftsRef.current.sections[id] = draft;
  };

  const template = useMemo(
    () => templates?.find((t) => t.id === templateId) ?? null,
    [templates, templateId],
  );
  const { data: presets } = usePromptPresets({ templateId: templateId || undefined });

  // sessionStorage または location.state から復元（MailListPage の「生成画面で編集」から）
  useEffect(() => {
    if (!templates) return; // テンプレ未ロード時は field id 変換できない
    const raw = sessionStorage.getItem('mail_restore');
    const stateRestore = (location.state as { restore?: RestoreState } | null)?.restore;
    let r: RestoreState | null = null;
    if (raw) {
      try {
        r = JSON.parse(raw) as RestoreState;
      } catch {
        /* ignore */
      }
      sessionStorage.removeItem('mail_restore'); // 1 回のみ
    } else if (stateRestore) {
      r = stateRestore;
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (!r) return;
    restoredTemplateIdRef.current = r.templateId || 'imported';
    setEditingMailId(r.mailId);
    setLang(r.lang);
    if (r.modelUsed) setModel(r.modelUsed);
    if (r.templateId && r.templateId !== 'imported') {
      setTemplateId(r.templateId); // 直接 setTemplateId（handleTemplateChange を通さない）
    }
    const tpl = templates.find((t) => t.id === r.templateId);
    const byId: Record<string, string> = {};
    if (tpl) {
      tpl.fields.forEach((f) => {
        byId[f.id] = r.formData[f.label] ?? '';
      });
    }
    setFormValues(byId);
    setResult({
      subject: r.subject,
      sections: r.sections,
      overview: r.overview,
      field_comments: r.field_comments,
    });
  }, [templates, location, navigate, setResult]);

  // テンプレ変更時のクリアは Select の onValueChange で直接ハンドル
  // （useEffect[templateId] を使うと初回マウント時に復元直後の値がクリアされるバグを回避）
  const handleTemplateChange = (newTid: string) => {
    if (newTid === templateId) return;
    setTemplateId(newTid);
    setFormValues({});
    setResult(null);
    setPresetId('');
    setEditingMailId(null);
    restoredTemplateIdRef.current = null;
  };

  const applyPreset = (id: string) => {
    setPresetId(id);
    if (!id) return;
    const preset = presets?.find((p) => p.id === id);
    if (!preset) return;
    setFormValues({ ...preset.values });
    toast.success(`プレセット「${preset.name}」を適用しました`);
  };

  const hasFormValues = Object.values(formValues).some((v) => v && v.trim());

  const handleGenerate = async () => {
    if (!templateId) {
      toast.error('テンプレートを選択してください');
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const res = await generateFn({ templateId, formValues, lang, model });
      setResult(res.data.generated);
      toast.success(`生成しました (${res.data.modelUsed})`);
    } catch (err) {
      toast.error(`生成に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveAsDraft = async (overwrite: boolean) => {
    // 確定（✅ / 窓外クリック）していない入力も保存に含める
    const current = commitPendingDrafts();
    if (!current) return;
    setSavingMail(true);
    try {
      const data = {
        subject: current.subject,
        sections: current.sections,
        overview: current.overview,
        field_comments: current.field_comments,
        templateId: templateId || 'imported',
        formData: template
          ? Object.fromEntries(template.fields.map((f) => [f.label, formValues[f.id] ?? '']))
          : {},
        lang,
        modelUsed: model,
      };
      if (overwrite && editingMailId) {
        await updateMail.mutateAsync({ id: editingMailId, data });
        toast.success('メールを上書き保存しました');
      } else {
        await createMail.mutateAsync({
          ...data,
          status: 'draft',
          note: '',
          sectionComments: {},
          projectIds: [],
        });
        toast.success('メールを下書きとして保存しました');
        setEditingMailId(null);
      }
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSavingMail(false);
    }
  };

  const handleImported = (
    imported: { subject: string; sections: MailSection[]; overview: string },
    modelUsed: string,
  ) => {
    setResult({
      subject: imported.subject,
      sections: imported.sections,
      overview: imported.overview,
      field_comments: {},
    });
    setModel(modelUsed);
  };

  return (
    <Screen
      title="メール作成/編集"
      description={
        editingMailId
          ? '保存済みメールを編集中（上書き保存 or 新規として保存できます）'
          : 'テンプレートとフォーム入力から Claude がメールを生成します'
      }
      actions={
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate('/mail?hub=list')}
            title="メール一覧に戻る"
          >
            <ArrowLeft />
            メール一覧に戻る
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setImportDialogOpen(true)}
          >
            <FileInput />
            既存メールを取り込む
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 h-[calc(100vh-260px)]">
        {/* 左: フォーム + レビュー依頼 (v3.3) */}
        <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] overflow-hidden flex flex-col">
          <Tabs
            value={leftTab}
            onValueChange={(v) => setLeftTab(v as 'template' | 'review')}
            className="flex flex-col h-full"
          >
            <TabsList className="!mb-0 px-4 pt-2">
              <TabsTrigger value="template">
                <FileText className="size-3.5 mr-1" />
                テンプレート
              </TabsTrigger>
              <TabsTrigger value="review">
                <MessagesSquare className="size-3.5 mr-1" />
                レビュー依頼
              </TabsTrigger>
            </TabsList>
            <TabsContent value="template" className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <Label>テンプレート</Label>
                {tplLoading ? (
                  <Spinner />
                ) : (
                  <Select value={templateId} onValueChange={handleTemplateChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="テンプレートを選択" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates?.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} ({t.category})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {template && (
                <>
                  {presets && presets.length > 0 && (
                    <div>
                      <Label>プレセット読込</Label>
                      <Select value={presetId} onValueChange={applyPreset}>
                        <SelectTrigger>
                          <SelectValue placeholder="プレセットを選択して値を適用..." />
                        </SelectTrigger>
                        <SelectContent>
                          {presets.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              <Bookmark className="size-3 inline-block mr-1" />
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="border-t border-[var(--border)] pt-4">
                    <h3 className="text-sm font-semibold mb-3">入力項目</h3>
                    <div className="space-y-3">
                      {template.fields.map((f) => (
                        <div key={f.id}>
                          <Label htmlFor={`f-${f.id}`}>{f.label}</Label>
                          {f.type === 'textarea' ? (
                            <Textarea
                              id={`f-${f.id}`}
                              rows={3}
                              placeholder={f.placeholder}
                              value={formValues[f.id] ?? ''}
                              onChange={(e) =>
                                setFormValues({ ...formValues, [f.id]: e.target.value })
                              }
                            />
                          ) : f.type === 'select' ? (
                            <Select
                              value={formValues[f.id] ?? ''}
                              onValueChange={(v) => setFormValues({ ...formValues, [f.id]: v })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={f.placeholder} />
                              </SelectTrigger>
                              <SelectContent>
                                {(f.options ?? []).map((o) => (
                                  <SelectItem key={o} value={o}>
                                    {o}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              id={`f-${f.id}`}
                              placeholder={f.placeholder}
                              value={formValues[f.id] ?? ''}
                              onChange={(e) =>
                                setFormValues({ ...formValues, [f.id]: e.target.value })
                              }
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    {hasFormValues && (
                      <div className="mt-3 flex justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setPresetDialogOpen(true)}
                          title="現在の入力値をプレセットとして保存"
                        >
                          <Save />
                          プレセットとして保存
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-[var(--border)] pt-4 grid grid-cols-2 gap-3">
                    <div>
                      <Label>出力言語</Label>
                      <Select value={lang} onValueChange={(v) => setLang(v as MailLang)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ja">日本語</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="zh">中文</SelectItem>
                          <SelectItem value="ko">한국어</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>モデル</Label>
                      <Select value={model} onValueChange={setModel}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={CLAUDE_MODELS.sonnetDefault}>sonnet</SelectItem>
                          <SelectItem value={CLAUDE_MODELS.opus}>opus</SelectItem>
                          <SelectItem value={CLAUDE_MODELS.haiku}>haiku</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="primary"
                    className="w-full"
                    onClick={handleGenerate}
                    disabled={generating}
                  >
                    <Wand2 />
                    {generating ? '生成中...' : 'メールを生成'}
                  </Button>
                </>
              )}
            </TabsContent>

            {/* レビュー依頼タブ (v3.3) */}
            <TabsContent value="review" className="flex-1 overflow-y-auto p-4 space-y-4">
              {!editingMailId ? (
                <div className="text-xs text-[var(--text-muted)] py-8 text-center leading-relaxed">
                  レビュー依頼には先にメールを保存してください。
                  <br />
                  メール生成 → 上書き保存 or 新規として保存 でアクティブになります。
                </div>
              ) : (
                <>
                  <p className="text-[11px] text-[var(--text-muted)] leading-relaxed">
                    レビュアーにメール本文へのレビュー依頼通知を送ります。担当者は事前にメール設定で登録、または下記から自由入力できます。
                  </p>
                  <div>
                    <Label>担当者</Label>
                    <select
                      className="w-full h-[34px] rounded-sm border border-[var(--border)] bg-[var(--bg-input)] px-2 text-[13px]"
                      value={reviewerEmail}
                      onChange={(e) => {
                        const opt = reviewerOptions.find((x) => x.email === e.target.value);
                        setReviewerEmail(e.target.value);
                        setReviewerName(opt?.name ?? '');
                      }}
                    >
                      <option value="">-- 選択 --</option>
                      {reviewerOptions.map((s) => (
                        <option key={s.email} value={s.email}>
                          {s.name} &lt;{s.email}&gt;
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>担当者名 (自由記入可)</Label>
                    <Input value={reviewerName} onChange={(e) => setReviewerName(e.target.value)} />
                  </div>
                  <div>
                    <Label>コメント</Label>
                    <Textarea
                      rows={4}
                      value={reviewerComment}
                      onChange={(e) => setReviewerComment(e.target.value)}
                      placeholder="レビュアーに伝えたい内容"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="primary"
                    className="w-full"
                    onClick={handleRequestReview}
                    disabled={sending !== null || !reviewerEmail}
                  >
                    <Send />
                    {sending === 'review' ? '送信中…' : 'レビュー依頼を送信'}
                  </Button>
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* 右: 結果 */}
        <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] overflow-y-auto p-6">
          {generating ? (
            <div className="h-full flex flex-col items-center justify-center gap-3">
              <Spinner className="size-8 border-[3px]" />
              <p className="text-xs text-[var(--text-muted)]">Claude が生成中...</p>
            </div>
          ) : !result ? (
            <EmptyState
              icon={Sparkles}
              title="生成結果がここに表示されます"
              description="左でテンプレートを選択してフォームを入力してください"
            />
          ) : (
            <div className="space-y-4 fade-in">
              {/* ツールバー: 表示切替 + アクション */}
              <div className="flex items-center justify-between gap-2 pb-3 border-b border-[var(--border)]">
                <div className="flex rounded border border-[var(--border)] overflow-hidden text-xs">
                  <button
                    type="button"
                    onClick={() => setViewMode('text')}
                    className={cn(
                      'px-3 py-1.5 flex items-center gap-1.5 transition-colors',
                      viewMode === 'text'
                        ? 'bg-[var(--accent)] text-white'
                        : 'bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)]',
                    )}
                  >
                    <FileText className="size-3" />
                    テキスト
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('html')}
                    className={cn(
                      'px-3 py-1.5 flex items-center gap-1.5 transition-colors',
                      viewMode === 'html'
                        ? 'bg-[var(--accent)] text-white'
                        : 'bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)]',
                    )}
                  >
                    <CodeIcon className="size-3" />
                    HTML
                  </button>
                </div>
                <div className="flex gap-1 flex-wrap">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      const text =
                        viewMode === 'html'
                          ? toHtml({ subject: result.subject, sections: result.sections })
                          : toPlainText({ subject: result.subject, sections: result.sections });
                      try {
                        await navigator.clipboard.writeText(text);
                        toast.success('全体をコピーしました');
                      } catch {
                        toast.error('コピーに失敗しました');
                      }
                    }}
                    title="全体コピー"
                  >
                    <Clipboard />
                    全体コピー
                  </Button>
                  {viewMode === 'text' ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const url = buildMailto({
                          subject: result.subject,
                          sections: result.sections,
                        });
                        window.location.href = url;
                      }}
                      title="メールアプリで確認"
                    >
                      <ExternalLink />
                      メールアプリで確認
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        downloadEml(
                          { subject: result.subject, sections: result.sections },
                          `${result.subject.slice(0, 50) || 'mail'}.eml`,
                        );
                        toast.success('.eml をダウンロードしました');
                      }}
                      title="HTML メール (.eml) をダウンロード"
                    >
                      <Download />
                      .eml ダウンロード
                    </Button>
                  )}

                  {/* v3.3: テスト送信 + 配信予約 (MailListPage の MailDetailDialog から移管) */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleSendTest}
                    disabled={!editingMailId || sending !== null}
                    title={!editingMailId ? '先にメールを保存してください' : '自分宛にテスト送信'}
                  >
                    <MailIcon />
                    {sending === 'test' ? '送信中…' : '自分宛にテスト送信'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setScheduleOpen(true)}
                    disabled={!editingMailId || sending !== null}
                    title={!editingMailId ? '先にメールを保存してください' : '配信予約'}
                  >
                    <CalendarClock />
                    配信予約
                  </Button>
                </div>
              </div>

              {viewMode === 'text' ? (
                <>
                  <SubjectCard
                    subject={result.subject}
                    context={{
                      currentSubject: result.subject,
                      sections: result.sections,
                      overview: result.overview,
                      lang,
                    }}
                    onChange={(next) => updateResult((r) => ({ ...r, subject: next }))}
                    onDraftChange={(draft) => {
                      pendingDraftsRef.current.subject = draft;
                    }}
                  />

                  {result.overview && (
                    <Card>
                      <CardHeader>
                        <Label>概要</Label>
                      </CardHeader>
                      <CardContent>
                        <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed">
                          {result.overview}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleSectionDragEnd}
                  >
                    <SortableContext
                      items={result.sections.map((s) => s.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="pl-6">
                        {/* 先頭の隙間（最初のセクションの上） */}
                        {result.sections.length > 0 && (
                          <SectionGapInserter onInsert={(type) => insertSection(0, type)} />
                        )}
                        {result.sections.map((sec, idx) => (
                          <div key={sec.id}>
                            <SortableSection
                              section={sec}
                              context={{
                                currentSubject: result.subject,
                                sections: result.sections,
                                overview: result.overview,
                                lang,
                              }}
                              onChange={(next) =>
                                updateResult((r) => ({
                                  ...r,
                                  sections: r.sections.map((s) => (s.id === next.id ? next : s)),
                                }))
                              }
                              onDelete={() =>
                                updateResult((r) => ({
                                  ...r,
                                  sections: r.sections.filter((s) => s.id !== sec.id),
                                }))
                              }
                              onDraftChange={(draft) => setSectionDraft(sec.id, draft)}
                            />
                            {/* このセクション直下の隙間 */}
                            <SectionGapInserter onInsert={(type) => insertSection(idx + 1, type)} />
                          </div>
                        ))}
                        <AddSectionButton
                          onAdd={(type) => insertSection(result.sections.length, type)}
                        />
                      </div>
                    </SortableContext>
                  </DndContext>
                </>
              ) : (
                <div>
                  <Label className="mb-2">件名</Label>
                  <div className="rounded bg-[var(--bg-elevated)] border border-[var(--border)] px-3 py-2 mb-4 font-semibold text-[14px]">
                    {result.subject}
                  </div>
                  <Label className="mb-2">HTML プレビュー</Label>
                  <iframe
                    title="HTML preview"
                    srcDoc={toHtml({ subject: result.subject, sections: result.sections })}
                    className="w-full min-h-[500px] rounded border border-[var(--border)] bg-white"
                    sandbox=""
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 固定アクションバー（右側プレビューのスクロールに関係なく常時表示） */}
      {result && (
        <div className="mt-3 flex justify-end gap-2 py-3 px-4 rounded border border-[var(--border)] bg-[var(--bg-surface)] sticky bottom-0 z-10 backdrop-blur-sm">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setResult(null);
              setEditingMailId(null);
            }}
          >
            クリア
          </Button>
          {editingMailId && (
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleSaveAsDraft(true)}
              disabled={savingMail}
            >
              {savingMail ? '保存中...' : '上書き保存'}
            </Button>
          )}
          <Button
            type="button"
            variant={editingMailId ? 'secondary' : 'primary'}
            onClick={() => void handleSaveAsDraft(false)}
            disabled={savingMail}
          >
            {savingMail ? '保存中...' : editingMailId ? '新規として保存' : '下書きとして保存'}
          </Button>
        </div>
      )}

      {template && (
        <PresetFormDialog
          open={presetDialogOpen}
          onOpenChange={setPresetDialogOpen}
          seed={{ templateId, values: formValues }}
          scope="mail"
        />
      )}

      <ImportMailDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImported={handleImported}
      />

      {/* v3.3: 配信予約ダイアログ (MailListPage から移管) */}
      {editingMailId && (
        <ScheduleFormDialog
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          prefillMailId={editingMailId}
        />
      )}
    </Screen>
  );
}
